/**
 * ZON-275 — the cards on the Notifications page and the rows in a school's
 * Recent news card. Presentational only: data in, callbacks out.
 */
import { useEffect, useState, type ReactNode } from "react";
import { ArrowUpRight, CalendarDays, Flag, Plus, Star, Trophy } from "lucide-react";
import type { FitItem, FitSchool, NewsItem, NewsResult } from "@/services/schoolNews";
import { schoolInitials } from "@/components/school/schoolBrand";
import { isResult, isWin, placeLabel, placeTone, typeLabel, type PlaceTone } from "./newsFeed";

const TONE_CLASSES: Record<PlaceTone, string> = {
  win: "bg-primary text-forest",
  top: "bg-lime-soft text-primary-text",
  other: "bg-muted text-forest-soft",
  none: "",
};

// Same wording as My Schools (pipelineDesign.ts).
/** Fit-tier colors: Dream amber, Realistic lime, Safety blue (ZON-280). */
const TIER_PILL: Record<string, string> = {
  dream: "bg-amber-soft text-amber-text",
  realistic: "bg-lime-soft text-primary-text",
  safety: "bg-safety-soft text-safety-text",
};

const TIER_LABELS: Record<string, string> = {
  dream: "Dream",
  realistic: "Realistic",
  safety: "Safety",
};

const SHORT_DATE = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});
function shortDate(iso: string): string {
  return SHORT_DATE.format(new Date(iso));
}

function schoolName(item: NewsItem): string {
  return item.schoolName || "A school on your list";
}

const LOGO_SIZES = {
  sm: "h-5 w-5 rounded-md text-[7px]",
  md: "h-8 w-8 rounded-xl text-[11px]",
} as const;

/** A school's logo in a small tile; its initials when there is no logo or it fails to load. */
export function SchoolLogoMark({
  name,
  logoUrl,
  size = "sm",
}: {
  name: string;
  logoUrl: string | null | undefined;
  size?: keyof typeof LOGO_SIZES;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  const showImage = !!logoUrl && !failed;
  return (
    <span
      className={`grid shrink-0 place-items-center overflow-hidden border border-border bg-white font-extrabold text-forest-soft ${LOGO_SIZES[size]}`}
    >
      {showImage ? (
        <img
          src={logoUrl}
          alt={`${name} logo`}
          className="h-full w-full object-contain p-0.5"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">{schoolInitials(name)}</span>
      )}
    </span>
  );
}

/** The 32px tile at the left of a card: the school's logo when logos are on, else the icon. */
function CardTile({
  item,
  className,
  children,
}: {
  item: NewsItem;
  className: string;
  children: ReactNode;
}) {
  if (item.logoUrl) {
    return <SchoolLogoMark name={schoolName(item)} logoUrl={item.logoUrl} size="md" />;
  }
  return (
    <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-xl ${className}`}>
      {children}
    </span>
  );
}

/**
 * A school's name. With `onOpen` it is a button that opens the school's panel
 * (every school in the Notifications list); without it, plain text (a
 * school's own Recent news card).
 */
function SchoolName({ item, onOpen }: { item: NewsItem; onOpen?: (item: NewsItem) => void }) {
  if (!onOpen || !item.schoolName) return <>{schoolName(item)}</>;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      aria-label={`View ${item.schoolName}`}
      className="text-left font-[inherit] underline-offset-2 hover:underline"
    >
      {item.schoolName}
    </button>
  );
}

/** Called when the family opens an item's article (for analytics). */
type OnRead = (item: NewsItem) => void;

function ArticleLink({
  item,
  onRead,
  className = "",
}: {
  item: NewsItem;
  onRead?: OnRead;
  className?: string;
}) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onRead?.(item)}
      className={`inline-flex items-center gap-1 text-[11px] font-bold hover:underline ${className}`}
    >
      Read article <ArrowUpRight className="h-3 w-3" />
    </a>
  );
}

const BADGE_CLASSES =
  "inline-block min-w-[38px] shrink-0 rounded-md px-1.5 py-0.5 text-center font-mono text-[11px] font-bold";

/**
 * The placement pill. With `placeholder`, a result that has no stated team
 * placement (individual-only events, head-to-head matches, recaps with only a
 * score) gets a neutral dash pill of the same width, so a column of results
 * still lines up and no placement is invented.
 */
export function PlaceBadge({
  result,
  placeholder = false,
}: {
  result: NewsResult | null;
  placeholder?: boolean;
}) {
  const label = placeLabel(result);
  if (!label) {
    if (!placeholder) return null;
    return (
      <span
        data-testid="place-badge"
        aria-label="No team placement"
        className={`${BADGE_CLASSES} bg-muted text-subtle`}
      >
        –
      </span>
    );
  }
  return (
    <span
      data-testid="place-badge"
      className={`${BADGE_CLASSES} ${TONE_CLASSES[placeTone(result)]}`}
    >
      {label}
    </span>
  );
}

function TypeChip({ type }: { type: string }) {
  return (
    <span className="shrink-0 rounded-full bg-safety-soft px-2 py-0.5 text-[11px] font-bold text-safety-text">
      {typeLabel(type)}
    </span>
  );
}

/** One compact line: badge or chip, school and text, date, link. */
export function NewsRow({
  item,
  showSchool = true,
  onOpen,
  onRead,
}: {
  item: NewsItem;
  showSchool?: boolean;
  onOpen?: (item: NewsItem) => void;
  onRead?: OnRead;
}) {
  return (
    <div className="flex items-start gap-2.5 border-t border-muted py-2 first:border-t-0">
      {isResult(item) || isWin(item) ? (
        <PlaceBadge result={item.result} placeholder />
      ) : (
        <TypeChip type={item.type} />
      )}
      {showSchool && <SchoolLogoMark name={schoolName(item)} logoUrl={item.logoUrl} />}
      <p className="min-w-0 flex-1 text-[11px] leading-snug text-forest">
        {showSchool && (
          <strong className="font-bold">
            <SchoolName item={item} onOpen={onOpen} />
          </strong>
        )}
        {showSchool && <span aria-hidden="true"> · </span>}
        <span>{item.summary || item.title}</span>
      </p>
      <span className="shrink-0 text-[11px] text-subtle">{shortDate(item.publishedAt)}</span>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onRead?.(item)}
        aria-label={`Read article: ${item.title}`}
        className="shrink-0 text-primary-text"
      >
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

export function AlertCard({
  item,
  onDraft,
  onOpen,
  onRead,
}: {
  item: NewsItem;
  onDraft: (item: NewsItem) => void;
  onOpen?: (item: NewsItem) => void;
  onRead?: OnRead;
}) {
  const win = isWin(item);
  const shell = win
    ? "border border-border bg-white text-forest"
    : "border border-forest bg-forest text-sage";
  const muted = win ? "text-subtle" : "text-sage";
  return (
    <article className={`flex items-start gap-3 rounded-2xl p-4 ${shell}`}>
      <CardTile
        item={item}
        className={win ? "bg-muted text-primary-text" : "bg-primary text-forest"}
      >
        {win ? <Trophy className="h-4 w-4" /> : <Star className="h-4 w-4" />}
      </CardTile>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <strong className="font-display text-base font-extrabold tracking-[-0.025em]">
            <SchoolName item={item} onOpen={onOpen} />
          </strong>
          {win && <PlaceBadge result={item.result} />}
        </div>
        <p className="mt-1 text-[12px] leading-relaxed">{item.summary || item.title}</p>
        <p className={`mt-1 text-[11px] ${muted}`}>
          {typeLabel(item.type)} · {shortDate(item.publishedAt)}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onDraft(item)}
            className="rounded-lg bg-primary px-3 py-1.5 text-[11px] font-bold text-forest hover:bg-primary/90"
          >
            Draft email to coach
          </button>
          <ArticleLink item={item} onRead={onRead} className="text-primary-text" />
        </div>
      </div>
    </article>
  );
}

export function NewsRowCard({
  item,
  onOpen,
  onRead,
}: {
  item: NewsItem;
  onOpen?: (item: NewsItem) => void;
  onRead?: OnRead;
}) {
  return (
    <article className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4">
      <CardTile item={item} className="bg-muted text-forest-soft">
        <CalendarDays className="h-4 w-4" />
      </CardTile>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <strong className="font-display text-base font-extrabold tracking-[-0.025em] text-forest">
            <SchoolName item={item} onOpen={onOpen} />
          </strong>
          <TypeChip type={item.type} />
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-forest">{item.summary || item.title}</p>
        <div className="mt-2 flex items-center gap-3">
          <span className="text-[11px] text-subtle">{shortDate(item.publishedAt)}</span>
          <ArticleLink item={item} onRead={onRead} className="text-primary-text" />
        </div>
      </div>
    </article>
  );
}

const RESULTS_SHOWN = 3;

export function ResultsCard({
  items,
  onOpen,
  onRead,
}: {
  items: NewsItem[];
  onOpen?: (item: NewsItem) => void;
  onRead?: OnRead;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, RESULTS_SHOWN);
  const hidden = items.length - shown.length;
  const schoolCount = new Set(items.map(item => item.teamId)).size;
  return (
    <article className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted text-forest-soft">
        <Flag className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <strong className="font-display text-base font-extrabold tracking-[-0.025em] text-forest">
          Tournament results · {schoolCount} of your schools
        </strong>
        <div className="mt-1">
          {shown.map(item => (
            <NewsRow key={item.id} item={item} onOpen={onOpen} onRead={onRead} />
          ))}
        </div>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-1 text-[11px] font-bold text-primary-text hover:underline"
          >
            + {hidden} more
          </button>
        )}
      </div>
    </article>
  );
}

export function FitsCard({
  item,
  onAdd,
  onOpen,
  addingTeamIds,
}: {
  item: FitItem;
  onAdd: (school: FitSchool) => void;
  /** Open the school's panel, so a family can read about it before adding. */
  onOpen: (school: FitSchool) => void;
  addingTeamIds: Set<string>;
}) {
  const count = item.schools.length;
  return (
    <article className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted text-primary-text">
        <Plus className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <strong className="font-display text-base font-extrabold tracking-[-0.025em] text-forest">
          {count === 1 ? "1 new school fits your profile" : `${count} new schools fit your profile`}
        </strong>
        <p className="mt-1 text-[11px] text-subtle">
          Based on your latest results · {shortDate(item.publishedAt)}
        </p>
        <ul className="mt-2">
          {item.schools.map(school => {
            const name = school.schoolName || "School";
            return (
              <li
                key={school.teamId}
                className="flex items-center gap-2 border-t border-muted py-2 text-[12px] first:border-t-0"
              >
                <SchoolLogoMark name={name} logoUrl={school.logoUrl} />
                <button
                  type="button"
                  onClick={() => onOpen(school)}
                  aria-label={`View ${name}`}
                  className="min-w-0 flex-1 truncate text-left font-bold text-forest underline-offset-2 hover:text-primary-text hover:underline"
                >
                  {name}
                </button>
                {school.fitTier && (
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${TIER_PILL[school.fitTier] ?? TIER_PILL.realistic}`}
                  >
                    {TIER_LABELS[school.fitTier] ?? school.fitTier}
                  </span>
                )}
                <button
                  type="button"
                  disabled={addingTeamIds.has(school.teamId)}
                  onClick={() => onAdd(school)}
                  aria-label={`Add ${name} to My Schools`}
                  className="shrink-0 rounded-lg bg-primary px-2.5 py-1 text-[11px] font-bold text-forest hover:bg-primary/90 disabled:opacity-50"
                >
                  + Add
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </article>
  );
}
